import { useRef, useCallback, useEffect } from 'react';

const RINGTONE_URL = '/sounds/ringtone.mp3';
const NOTIFICATION_URL = '/sounds/notification.mp3';

export function useCallAudio() {
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const notificationRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio elements
  useEffect(() => {
    // Preload ringtone
    ringtoneRef.current = new Audio(RINGTONE_URL);
    ringtoneRef.current.loop = true;
    ringtoneRef.current.preload = 'auto';

    // Preload notification sound
    notificationRef.current = new Audio(NOTIFICATION_URL);
    notificationRef.current.preload = 'auto';

    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.src = '';
      }
      if (notificationRef.current) {
        notificationRef.current.pause();
        notificationRef.current.src = '';
      }
    };
  }, []);

  const playRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((error) => {
        console.warn('Failed to play ringtone:', error);
      });
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  const playNotification = useCallback(() => {
    if (notificationRef.current) {
      notificationRef.current.currentTime = 0;
      notificationRef.current.play().catch((error) => {
        console.warn('Failed to play notification:', error);
      });
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    if (ringtoneRef.current) {
      ringtoneRef.current.volume = clampedVolume;
    }
    if (notificationRef.current) {
      notificationRef.current.volume = clampedVolume;
    }
  }, []);

  return {
    playRingtone,
    stopRingtone,
    playNotification,
    setVolume,
  };
}
